/**
 * RoleDetailPage — Settings → Permissions & Roles → role detail.
 *
 * Five tabs:
 *  1. Overview (metadata: name / description / 2FA / external / expiry / purpose)
 *  2. Permissions (multi-select grouped by module, with SoD violation surface)
 *  3. Field Access (independent toggles for compensation / medical / etc.)
 *  4. Scopes (scope_type + scope_value rows)
 *  5. Members (read-only list of users assigned to this role)
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  CircularProgress,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Chip,
  Stack,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import { ArrowBack as BackIcon, Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import {
  adminRolesApi,
  AccessLevel,
  ScopeRow,
  RoleMember,
  SodViolation,
} from '@/api/endpoints/adminRoles';
import { settingsApi } from '@/api/endpoints/settings';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermission } from '@/hooks/usePermission';

// ── Field categories the UI exposes — matches the backend catalog ──
const FIELD_CATEGORIES: Array<{
  key: string;
  label: string;
  description: string;
  writeImpact: string;
}> = [
  {
    key: 'compensation',
    label: 'Compensation',
    description: 'Salary, allowances, bonuses, payslips',
    writeImpact: 'Can edit salaries, run payroll adjustments, and issue bonuses. Grants one of the highest-privilege financial capabilities — assign only to payroll admins.',
  },
  {
    key: 'medical',
    label: 'Medical',
    description: 'Medical certificates, sick leave details',
    writeImpact: 'Can upload and modify medical records. Sensitive health data — assign only to HR Managers or Occupational Health staff.',
  },
  {
    key: 'disciplinary',
    label: 'Disciplinary',
    description: 'Warnings, PIPs, terminations',
    writeImpact: 'Can issue warnings, create PIPs, and record termination reasons. Assign only to HR Managers or department heads with people-management authority.',
  },
  {
    key: 'bank_account',
    label: 'Bank account',
    description: 'Bank account numbers + routing details',
    writeImpact: 'Can change where salary is paid. High fraud-risk — assign only to Payroll Admins with dual-approval workflows in place.',
  },
  {
    key: 'tax_id',
    label: 'Tax ID / NIN / BVN',
    description: 'Statutory identifiers',
    writeImpact: 'Can update government-issued IDs used for tax filing and compliance. Changes trigger re-filing obligations — assign only to HR or Compliance staff.',
  },
  {
    key: 'personal_documents',
    label: 'Personal documents',
    description: 'Contracts, ID scans, certificates',
    writeImpact: 'Can upload, replace, or delete employment contracts and identity documents. Assign only to HR Admins managing onboarding and offboarding workflows.',
  },
  {
    key: 'performance_reviews',
    label: 'Performance reviews',
    description: 'Quarterly + annual reviews',
    writeImpact: 'Can create and submit performance evaluations. Assign to line managers or HR Business Partners — not to peers of the reviewee.',
  },
  {
    key: 'salary_history',
    label: 'Salary history',
    description: 'All historical salary changes',
    writeImpact: 'Can backdate or correct historical salary records. Corrections affect payroll reconciliation and audit trails — assign only to Payroll Admins.',
  },
];

const SCOPE_TYPES: Array<{ key: string; label: string; description: string }> = [
  { key: 'self_only', label: 'Self only', description: "User sees only their own records" },
  { key: 'direct_department', label: 'Direct department', description: 'Heads of department only see their team' },
  { key: 'business_unit', label: 'Business unit', description: 'Restrict to assigned business unit' },
  { key: 'gl_account_range', label: 'GL account range', description: 'Restrict accounting to a code range' },
  { key: 'cost_center', label: 'Cost center', description: 'Restrict to a specific cost center' },
  { key: 'candidate_status', label: 'Candidate status', description: 'Recruiter scope — applicant/offered only' },
];

export default function RoleDetailPage() {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notify = useNotificationStore((s) => s.show);
  const { hasPermission } = usePermission();
  const canWrite = hasPermission('settings.roles.write');

  const [tab, setTab] = useState(0);
  const [draftMeta, setDraftMeta] = useState<{
    name: string;
    description: string;
    requires_2fa: boolean;
    is_external: boolean;
    purpose_of_access: string;
  } | null>(null);

  const [draftPerms, setDraftPerms] = useState<Set<string> | null>(null);
  const [draftFieldPerms, setDraftFieldPerms] = useState<Record<string, AccessLevel> | null>(null);
  const [draftScopes, setDraftScopes] = useState<ScopeRow[] | null>(null);
  const [sodViolations, setSodViolations] = useState<SodViolation[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data: role, isLoading } = useQuery({
    queryKey: ['admin-role', roleId],
    queryFn: async () => (await adminRolesApi.detail(roleId!)).data,
    enabled: !!roleId,
  });

  const { data: members = [] } = useQuery({
    queryKey: ['admin-role-members', roleId],
    queryFn: async () => (await adminRolesApi.members(roleId!)).data,
    enabled: !!roleId,
  });

  const { data: allPermissions = [] } = useQuery({
    queryKey: ['settings-permissions'],
    queryFn: async () => (await settingsApi.listPermissions()).data,
  });

  // Initialise drafts when role loads or we switch tabs.
  useEffect(() => {
    if (!role) return;
    setDraftMeta({
      name: role.name,
      description: role.description ?? '',
      requires_2fa: role.requires_2fa,
      is_external: role.is_external,
      purpose_of_access: role.purpose_of_access ?? '',
    });
    setDraftPerms(new Set(role.permission_codes));
    const fpMap: Record<string, AccessLevel> = {};
    role.field_permissions.forEach((fp: { field_category: string; access_level: AccessLevel }) => {
      fpMap[fp.field_category] = fp.access_level;
    });
    setDraftFieldPerms(fpMap);
    setDraftScopes(role.scopes);
  }, [role]);

  const editable = canWrite && role && !role.is_template && !role.is_system;

  // ── Mutations ────────────────────────────────────────────────────

  const updateMetaMutation = useMutation({
    mutationFn: async () => {
      if (!draftMeta || !roleId) return;
      return (
        await adminRolesApi.update(roleId, {
          name: draftMeta.name,
          description: draftMeta.description,
          requires_2fa: draftMeta.requires_2fa,
          is_external: draftMeta.is_external,
          purpose_of_access: draftMeta.purpose_of_access,
        })
      ).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-role', roleId] });
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      notify('Role saved', 'success');
    },
    onError: (err: any) => {
      notify(err?.response?.data?.detail ?? 'Save failed', 'error');
    },
  });

  const updatePermsMutation = useMutation({
    mutationFn: async () => {
      if (!draftPerms || !roleId) return;
      // Send the version we loaded so a concurrent edit is rejected (409
      // stale_role_version) instead of silently overwritten.
      return (
        await adminRolesApi.replacePermissions(roleId, [...draftPerms], role?.version_id)
      ).data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-role', roleId] });
      setSodViolations(data?.sod_violations ?? []);
      const warnCount = (data?.sod_violations ?? []).length;
      notify(
        warnCount > 0
          ? `Saved with ${warnCount} segregation-of-duties warning${warnCount === 1 ? '' : 's'}.`
          : 'Permissions saved',
        warnCount > 0 ? 'warning' : 'success',
      );
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail;
      if (detail?.code === 'sod_violation') {
        setSodViolations(detail.violations || []);
        notify('Blocked: violates a segregation-of-duties rule. See details below.', 'error');
      } else {
        notify(typeof detail === 'string' ? detail : 'Save failed', 'error');
      }
    },
  });

  const updateFieldPermsMutation = useMutation({
    mutationFn: async () => {
      if (!draftFieldPerms || !roleId) return;
      return (
        await adminRolesApi.replaceFieldPermissions(roleId, draftFieldPerms, role?.version_id)
      ).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-role', roleId] });
      notify('Field access saved', 'success');
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: async () => {
      if (!roleId) throw new Error('No roleId');
      return (await adminRolesApi.delete(roleId)).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      notify('Role deleted', 'success');
      navigate('/settings/roles');
    },
    onError: (err: any) => {
      notify(err?.response?.data?.detail ?? 'Delete failed', 'error');
    },
  });

  const updateScopesMutation = useMutation({
    mutationFn: async () => {
      if (!draftScopes || !roleId) return;
      return (await adminRolesApi.replaceScopes(roleId, draftScopes, role?.version_id)).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-role', roleId] });
      notify('Scopes saved', 'success');
    },
  });

  // ── Permission grouping by module prefix ────────────────────────
  const permsByModule = useMemo(() => {
    const grouped: Record<string, typeof allPermissions> = {};
    allPermissions.forEach((p: any) => {
      const module = p.code.split('.')[0] ?? 'other';
      if (!grouped[module]) grouped[module] = [];
      grouped[module].push(p);
    });
    return grouped;
  }, [allPermissions]);

  if (isLoading || !role) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/settings/roles')} size="small">
          <BackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
          {role.name}
        </Typography>
        {role.is_system && <Chip label="System" />}
        {role.is_template && !role.is_system && (
          <Chip label="Template (read-only)" color="primary" variant="outlined" />
        )}
        {editable && role.member_count === 0 && (
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            size="small"
            onClick={() => setDeleteOpen(true)}
          >
            Delete role
          </Button>
        )}
      </Stack>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs">
        <BrandedDialogTitle>Delete role</BrandedDialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete <strong>{role.name}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteRoleMutation.isPending}
            onClick={() => deleteRoleMutation.mutate()}
          >
            {deleteRoleMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {!editable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {role.is_system
            ? 'System roles cannot be edited.'
            : 'Templates are read-only — clone this role to edit it.'}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label={`Permissions (${role.permission_count})`} />
        <Tab label="Field access" />
        <Tab label="Scopes" />
        <Tab label={`Members (${role.member_count})`} />
      </Tabs>

      {/* ── Tab 0: Overview ───────────────────────────────────── */}
      {tab === 0 && draftMeta && (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <TextField
                label="Name"
                fullWidth
                value={draftMeta.name}
                disabled={!editable}
                onChange={(e) => setDraftMeta({ ...draftMeta, name: e.target.value })}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={draftMeta.description}
                disabled={!editable}
                onChange={(e) => setDraftMeta({ ...draftMeta, description: e.target.value })}
              />
              <TextField
                label="Purpose of access"
                helperText="Why does this role access personal data? Surfaced in audit log (NDPA / POPIA / Kenya DPA compliance)."
                fullWidth
                multiline
                rows={2}
                value={draftMeta.purpose_of_access}
                disabled={!editable}
                onChange={(e) =>
                  setDraftMeta({ ...draftMeta, purpose_of_access: e.target.value })
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={draftMeta.requires_2fa}
                    disabled={!editable}
                    onChange={(e) =>
                      setDraftMeta({ ...draftMeta, requires_2fa: e.target.checked })
                    }
                  />
                }
                label="Require 2FA — block sessions without TOTP enrolled"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={draftMeta.is_external}
                    disabled={!editable}
                    onChange={(e) =>
                      setDraftMeta({ ...draftMeta, is_external: e.target.checked })
                    }
                  />
                }
                label="External role — every read is denial-logged (e.g. external auditor)"
              />
              {role.expires_at && (
                <Alert severity="info">
                  This role expires on {new Date(role.expires_at).toLocaleString()}.
                  Assignments are auto-revoked at 04:00 UTC daily.
                </Alert>
              )}
            </Stack>
            {editable && (
              <Box sx={{ mt: 3, textAlign: 'right' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={updateMetaMutation.isPending}
                  onClick={() => updateMetaMutation.mutate()}
                >
                  Save overview
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Tab 1: Permissions ───────────────────────────────── */}
      {tab === 1 && draftPerms && (
        <Stack spacing={2}>
          {sodViolations.length > 0 && (
            <Alert severity="warning">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Segregation-of-duties review
              </Typography>
              <Stack spacing={1}>
                {sodViolations.map((v) => (
                  <Box key={v.rule_name}>
                    <strong>{v.rule_name}</strong> ({v.severity}) — {v.description}
                    {v.rationale && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {v.rationale}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Alert>
          )}

          {Object.keys(permsByModule)
            .sort()
            .map((module) => (
              <Card key={module}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    {module}
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Stack spacing={0.5}>
                    {permsByModule[module].map((p: any) => (
                      <FormControlLabel
                        key={p.code}
                        control={
                          <Switch
                            size="small"
                            checked={draftPerms.has(p.code)}
                            disabled={!editable}
                            onChange={(e) => {
                              const next = new Set(draftPerms);
                              if (e.target.checked) next.add(p.code);
                              else next.delete(p.code);
                              setDraftPerms(next);
                            }}
                          />
                        }
                        label={
                          <Stack>
                            <Typography variant="body2">{p.code}</Typography>
                            {p.description && (
                              <Typography variant="caption" color="text.secondary">
                                {p.description}
                              </Typography>
                            )}
                          </Stack>
                        }
                      />
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            ))}

          {editable && (
            <Box sx={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={updatePermsMutation.isPending}
                onClick={() => updatePermsMutation.mutate()}
              >
                Save permissions
              </Button>
            </Box>
          )}
        </Stack>
      )}

      {/* ── Tab 2: Field access ──────────────────────────────── */}
      {tab === 2 && draftFieldPerms && (
        <Card>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Field-level toggles override module permissions for sensitive data. Setting a
              category to <strong>Hidden</strong> blocks access even if the underlying module
              permission is granted.
            </Alert>
            <Stack spacing={2}>
              {FIELD_CATEGORIES.map((cat) => (
                <Stack
                  key={cat.key}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {cat.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cat.description}
                    </Typography>
                    {(draftFieldPerms[cat.key] ?? 'none') === 'write' && (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
                      >
                        ⚠ Write: {cat.writeImpact}
                      </Typography>
                    )}
                  </Box>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={draftFieldPerms[cat.key] ?? 'none'}
                    disabled={!editable}
                    onChange={(_, v) => {
                      if (v === null) return;
                      setDraftFieldPerms({ ...draftFieldPerms, [cat.key]: v });
                    }}
                  >
                    <ToggleButton value="none">Hidden</ToggleButton>
                    <ToggleButton value="read">Read</ToggleButton>
                    <ToggleButton value="write">Write</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              ))}
            </Stack>
            {editable && (
              <Box sx={{ mt: 3, textAlign: 'right' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={updateFieldPermsMutation.isPending}
                  onClick={() => updateFieldPermsMutation.mutate()}
                >
                  Save field access
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Tab 3: Scopes ────────────────────────────────────── */}
      {tab === 3 && draftScopes && (
        <Card>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Scopes restrict which rows the role can see. Without scopes, RLS-based company
              isolation still applies — these add row-level filtering inside a company.
            </Alert>
            <Stack spacing={2}>
              {draftScopes.map((s, idx) => (
                <Stack key={idx} direction="row" alignItems="center" spacing={2}>
                  <Select
                    size="small"
                    value={s.scope_type}
                    disabled={!editable}
                    onChange={(e) => {
                      const next = [...draftScopes];
                      next[idx] = { ...s, scope_type: e.target.value };
                      setDraftScopes(next);
                    }}
                    sx={{ minWidth: 200 }}
                  >
                    {SCOPE_TYPES.map((t) => (
                      <MenuItem key={t.key} value={t.key}>
                        <Tooltip title={t.description}>
                          <Box>{t.label}</Box>
                        </Tooltip>
                      </MenuItem>
                    ))}
                  </Select>
                  <TextField
                    size="small"
                    label="Scope value (JSON)"
                    fullWidth
                    value={JSON.stringify(s.scope_value)}
                    disabled={!editable}
                    onChange={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value || '{}');
                        const next = [...draftScopes];
                        next[idx] = { ...s, scope_value: parsed };
                        setDraftScopes(next);
                      } catch {
                        // ignore parse errors during typing
                      }
                    }}
                  />
                  <Select
                    size="small"
                    value={s.modifier}
                    disabled={!editable}
                    onChange={(e) => {
                      const next = [...draftScopes];
                      next[idx] = { ...s, modifier: e.target.value as 'include' | 'exclude' };
                      setDraftScopes(next);
                    }}
                  >
                    <MenuItem value="include">Include</MenuItem>
                    <MenuItem value="exclude">Exclude</MenuItem>
                  </Select>
                  <IconButton
                    color="error"
                    disabled={!editable}
                    onClick={() => setDraftScopes(draftScopes.filter((_, i) => i !== idx))}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))}
              {editable && (
                <Button
                  variant="outlined"
                  onClick={() =>
                    setDraftScopes([
                      ...draftScopes,
                      {
                        scope_type: 'self_only',
                        scope_value: {},
                        modifier: 'include',
                        company_id: null,
                      },
                    ])
                  }
                >
                  + Add scope
                </Button>
              )}
            </Stack>
            {editable && (
              <Box sx={{ mt: 3, textAlign: 'right' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={updateScopesMutation.isPending}
                  onClick={() => updateScopesMutation.mutate()}
                >
                  Save scopes
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Tab 4: Members ───────────────────────────────────── */}
      {tab === 4 && (
        <Card>
          <CardContent>
            {members.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No users currently hold this role.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {members.map((m: RoleMember) => (
                  <Stack
                    key={`${m.user_id}-${m.company_id}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      <strong>{m.full_name}</strong>{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        {m.email}
                      </Typography>
                    </Typography>
                    {m.is_group_level && <Chip label="Group" size="small" color="primary" />}
                    {m.last_login_at && (
                      <Typography variant="caption" color="text.secondary">
                        Last login: {new Date(m.last_login_at).toLocaleDateString()}
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
